/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, observable, makeObservable } from 'mobx';
import { parseMfaToken } from '@veeam-vspc/shared/helpers';
import { HttpMethods } from '@veeam-vspc/shared/core';

import type { IdentityProvider } from '@veeam-vspc/models/rest';
import type { NextFactor, SuccessAuth } from '@veeam-vspc/shared/services';

import { LoginFactors } from '../enums';
import { QueryKeys } from 'core/enums';
import { CurrentLoginFactor, GoToLoginStartResponse } from '../interfaces';

import type {
    CloudConnectServerItem,
    FinishFactorResponse,
    GoToResetPasswordResponse,
    GoToSsoResponse,
    LoginParams,
} from '../interfaces';

enum MfaLoginTypes {
    // @TODO Change to enum from models when backend add it
    SelectServer = 'select_server',
    Totp = 'totp',
}

export class LoginStore {

    @observable error: string | null;
    @observable warning: string | null;
    @observable info: string | null;
    @observable isLoading = false;
    @observable currentFactor: CurrentLoginFactor;

    productImageSrc: string;
    productLogoImageSrc: string;

    identityProviders: IdentityProvider[] | null;

    loadApp: () => void;
    loadLogin: (homeUrl?: string) => void;

    constructor({ identityProviders, error = null, warning = null, info = null, loadApp, loadLogin }: LoginParams) {
        makeObservable(this);

        this.productImageSrc = '/Branding/GetPortalLoginImageBrandImage';
        this.productLogoImageSrc = '/Branding/GetPortalLogoBrandImage';
        this.error = error;
        this.warning = warning;
        this.info = info;
        this.loadApp = loadApp;
        this.loadLogin = loadLogin;
        this.identityProviders = identityProviders; //d

        this.initDefaultFactor({ error, warning, info, goToLoginStart: true });
    }

    @action.bound
    setIsLoading(loading: boolean): void {
        this.isLoading = loading;
    }

    @action.bound
    setError(error: string): void {
        this.error = error;
    }

    @action.bound
    setWarning(warning: string): void {
        this.warning = warning;
    }

    @action.bound
    setInfo(info: string): void {
        this.info = info;
    }

    @action.bound
    setCurrentFactor(currentFactor: CurrentLoginFactor): void {
        this.currentFactor = currentFactor;
    }

    @action.bound
    onFinishFactorHandler(resp: FinishFactorResponse & { login?: string; }): void {
        this.setIsLoading(false);
        this.resetMessages();

        if ((resp as SuccessAuth).successAuth) {
            return this.loadApp();
        } else if ((resp as GoToSsoResponse).goToSso) {
            return this.goToSsoFactor();
        } else if ((resp as GoToResetPasswordResponse).goToResetPassword) {
            return this.goToResetPasswordFactor();
        } else if ((resp as NextFactor).nextFactor) {
            return this.goToNextFactor((resp as NextFactor).mfaToken, resp.login);
        } else if ((resp as GoToLoginStartResponse).goToLoginStart) {
            return this.goToLoginStart(resp as GoToLoginStartResponse);
        }

        console.error('Wrong login factor. Change to LoginStart');
    }

    @action.bound
    onSsoClick(): void {
        this.setIsLoading(true);

        const form = document.createElement('form');

        form.method = HttpMethods.Post;
        form.action = `/Saml2/${this.identityProviders[0].name}/SignIn`;

        document.body.appendChild(form);

        form.submit();
    }

    @action.bound
    resetMessages(): void {
        if (this.error) {
            this.setError(null);
        }

        if (this.warning) {
            this.setWarning(null);
        }

        if (this.info) {
            this.setInfo(null);
        }
    }

    protected initDefaultFactor(factorData: GoToLoginStartResponse): void {
        const code = new URLSearchParams(window.location.search).get(QueryKeys.ResetPasswordFlag);

        if (code) {
            this.goToChangePasswordFactor(code);
        } else {
            this.goToLoginStart(factorData);
        }
    }

    @action.bound
    protected goToNextFactor(mfaToken: string, login: string): void {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { mfa_type, select_server } = parseMfaToken(mfaToken);

        if (mfa_type === MfaLoginTypes.SelectServer) {
            this.setCurrentFactor({
                type: LoginFactors.SelectServer,
                mfaToken,
                cloudConnectServerItems: select_server.split(';').map((name: string): CloudConnectServerItem => ({ name })),
                login,
            });
        } else if (mfa_type === MfaLoginTypes.Totp) {
            this.setCurrentFactor({
                type: LoginFactors.VerifyCode,
                mfaToken,
                login,
                attempts: 0,
            });
        } else {
            console.error('Cannot define next factor type. Change to LoginStart', mfaToken);
            this.goToLoginStart();
            throw Error('Wrong token');
        }
    }

    @action.bound
    protected goToLoginStart({ error = null, warning = null, info = null }: GoToLoginStartResponse = { goToLoginStart: true }): void {
        this.setCurrentFactor({
            type: LoginFactors.LoginStart,
            identityProviders: this.identityProviders,
        });
        this.setError(error);
        this.setWarning(warning);
        this.setInfo(info);
    }

    @action.bound
    protected goToSsoFactor(): void {
        this.setCurrentFactor({ type: LoginFactors.Sso });
    }

    @action.bound
    protected goToChangePasswordFactor(code: string): void {
        this.setCurrentFactor({
            type: LoginFactors.ChangePassword,
            code,
        });
    }

    @action.bound
    protected goToResetPasswordFactor(): void {
        this.setCurrentFactor({ type: LoginFactors.ResetPassword });
    }

}
